/**
 * =============================================================================
 * IDS CRM Web - Engineer bulletins: who may do what, and how it reads
 * =============================================================================
 *
 * Gareth 2026-09-11: "a bulletin area where we can share info with the
 * engineers ... if we see a repeated fault and we work out how to fix it".
 *
 * Client mirror of the rules in api/src/services/bulletins.js. The API is the
 * real gate; these only decide which buttons to show, so a button never
 * appears that would come back refused.
 *
 *  - Anyone signed in writes a draft. Team leaders, managers and admins review
 *    and publish (Gareth 2026-09-13: "team leads - yes").
 *  - A draft is edited or discarded by its author or a reviewer. A published
 *    bulletin is never edited; a newer one replaces it.
 * =============================================================================
 */

/** Mirror of the API's ROLES.OPS, which is what isReviewer checks. */
export const REVIEWER_ROLES = ['team_leader', 'manager', 'admin'];

export const TITLE_MAX_LENGTH = 200;
export const BODY_MAX_LENGTH = 20000;
export const MAX_BULLETIN_ATTACHMENTS = 10;

/**
 * New files travel as base64 inside one JSON body, and base64 is a third
 * bigger than the file. About 7 MB of new files is what fitted in one save
 * while the API capped that body at 10 MB; more has to go in a second save. It
 * is also the most one file can be: a single file over it could never be saved
 * at all. The cap went to 25 MB on 2026-09-21 so that several phone photos fit
 * in one save of a job or ticket (ONE_SAVE_BUDGET_BYTES in utils/attachments.js,
 * 18 MB). Bulletins were not part of that decision and keep 7 MB.
 */
export const NEW_FILES_BUDGET_BYTES = 7 * 1024 * 1024;

/** /auth/me and the login response say userId; older test fixtures say user_id. */
export function currentUserId(user) {
  return (user && (user.userId || user.user_id)) || null;
}

export function isBulletinReviewer(user) {
  return Boolean(user && REVIEWER_ROLES.includes(user.role));
}

export function isBulletinAuthor(user, bulletin) {
  const userId = currentUserId(user);
  return Boolean(userId && bulletin && bulletin.created_by === userId);
}

/** Only a draft is editable, and only by the person who wrote it or a reviewer. */
export function canEditBulletin(user, bulletin) {
  if (!bulletin || bulletin.status !== 'draft') return false;
  return isBulletinAuthor(user, bulletin) || isBulletinReviewer(user);
}

/**
 * What the status is called on screen. "Superseded" is the stored word; the
 * engineers are told a bulletin was "Replaced", which is what happened to it.
 */
export function bulletinStatusLabel(bulletin) {
  const status = bulletin && bulletin.status;
  if (status === 'draft') return bulletin.submitted_at ? 'Waiting for review' : 'Draft';
  if (status === 'published') return 'Published';
  if (status === 'superseded') return 'Replaced';
  return status ? String(status).replace(/_/g, ' ') : 'Unknown';
}

/** A CSS-safe name for the badge colour, one per label above. */
export function bulletinStatusTone(bulletin) {
  const status = bulletin && bulletin.status;
  if (status === 'draft') return bulletin.submitted_at ? 'review' : 'draft';
  if (status === 'published') return 'published';
  if (status === 'superseded') return 'replaced';
  return 'unknown';
}

/** Bytes of the files being added in this save; kept files are already stored. */
export function newAttachmentBytes(files) {
  return (Array.isArray(files) ? files : [])
    .filter(file => file && file.dataUrl)
    .reduce((total, file) => {
      if (Number.isFinite(file.size)) return total + file.size;
      // No size recorded: estimate from the base64, which is 4 characters per 3 bytes.
      const encoded = String(file.dataUrl).split(',')[1] || '';
      return total + Math.floor((encoded.length * 3) / 4);
    }, 0);
}

export function formatFileSize(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '';
  if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024))} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

/** The line shown after "Send for review". */
export function submitNotice(result = {}) {
  if (result.already_submitted) {
    return 'This was already sent for review. A team leader or manager will publish it.';
  }
  const notified = Number(result.notified) || 0;
  if (notified === 0) {
    return "Sent for review, but no reviewers could be messaged. Tell your team leader it's waiting.";
  }
  return `Sent to ${notified} reviewer${notified === 1 ? '' : 's'}.`;
}

/** The line shown after publishing: who was told, and what it replaced. */
export function publishNotice(result = {}) {
  const notified = Number(result.notified) || 0;
  let text = notified === 0
    ? 'Published. Nobody notified.'
    : `Published. ${notified} ${notified === 1 ? 'person' : 'people'} notified.`;
  const replaced = (Array.isArray(result.superseded) ? result.superseded : [])
    .map(entry => entry && entry.title)
    .filter(Boolean);
  if (replaced.length) {
    text += ` Replaced: ${replaced.map(title => `'${title}'`).join(', ')}.`;
  }
  return text;
}
